import { FunctionComponent, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";

import { AuthGuard } from "@dw/components";
import { LazyLoader } from "@dw/components/core";
import { ROUTES } from "@dw/constants";
import { getRouteName } from "@dw/utils";

import { AuthContextProvider } from "./contexts";
import { ErrorBoundary } from "./ErrorBoundary";

const LoginPage = LazyLoader(lazy(() => import("./pages/LoginPage")));

const DashboardPage = LazyLoader(lazy(() => import("./pages/DashboardPage")));
const ManageDanceSchoolPage = LazyLoader(lazy(() => import("./pages/ManageDanceSchoolPage")));
const ManageLocationPage = LazyLoader(lazy(() => import("./pages/ManageLocationPage")));
const ManageDanceClassPage = LazyLoader(lazy(() => import("./pages/ManageDanceClassPage")));

const ManageMyProfilePage = LazyLoader(lazy(() => import("./pages/ManageMyProfilePage")));

const ManageStudentPage = LazyLoader(lazy(() => import("./pages/ManageStudentPage")));
const ViewDancerAccountPage = LazyLoader(lazy(() => import("./pages/ViewDancerAccountPage")));

const ViewCommunicationDetailsPage = LazyLoader(lazy(() => import('./pages/ViewCommunicationDetailsPage')));

const RegisterTrialClassPage = LazyLoader(lazy(() => import('./pages/RegisterTrialClassPage')));
const BulkInvoicesPage = LazyLoader(lazy(() => import('./pages/BulkInvoicesPage')));
const ManageExamPage = LazyLoader(lazy(() => import('./pages/ManageExamPage')));
const ManageExamClassPage = LazyLoader(lazy(() => import('./pages/ManageExamClassPage')));

// Public (unauthenticated) invoice view — intentionally NOT wrapped in AuthContextProvider/AuthGuard.
const PublicInvoiceViewPage = LazyLoader(lazy(() => import('./pages/PublicInvoiceViewPage')));

// Public examiner scoring — token-authed via X-Examiner-Token, no AuthContextProvider/AuthGuard.
const ExaminerHomePage = LazyLoader(lazy(() => import('./pages/ExaminerHomePage')));
const ExaminerClassPage = LazyLoader(lazy(() => import('./pages/ExaminerClassPage')));

export const AppRouter: FunctionComponent = () => {
  const routes = createBrowserRouter([
    {
      element: (
        <AuthContextProvider>
          <ErrorBoundary>
            <LoginPage />
          </ErrorBoundary>
        </AuthContextProvider>
      ),
      index: true,
    },
    {
      path: getRouteName(ROUTES.Dashboard),
      element: (
        <AuthContextProvider>
          <ErrorBoundary>
            <AuthGuard>
              <DashboardPage />
            </AuthGuard>
          </ErrorBoundary>
        </AuthContextProvider>
      ),
      children: [
        {
          path: getRouteName(ROUTES.ManageDanceSchool, ROUTES.Dashboard),
          element: <ManageDanceSchoolPage />,
        },
        {
          path: getRouteName(ROUTES.ManageLocation, ROUTES.Dashboard),
          element: <ManageLocationPage />,
        },
        {
          path: getRouteName(ROUTES.ManageDanceClass, ROUTES.Dashboard),
          element: <ManageDanceClassPage />,
        },
        {
          path: getRouteName(ROUTES.ManageMyProfile, ROUTES.Dashboard),
          element: <ManageMyProfilePage />,
        },
        {
          path: getRouteName(ROUTES.CreateStudent, ROUTES.Dashboard),
          element: <ManageStudentPage />,
        },
        {
          path: getRouteName(ROUTES.ManageStudent, ROUTES.Dashboard),
          element: <ManageStudentPage />,
        },
        {
          path: getRouteName(ROUTES.ViewDancerAccount, ROUTES.Dashboard),
          element: <ViewDancerAccountPage />,
        },
        {
          path: getRouteName(ROUTES.ViewCommunicationDetails, ROUTES.Dashboard),
          element: <ViewCommunicationDetailsPage />,
        },
        {
          path: getRouteName(ROUTES.RegisterTrialClass, ROUTES.Dashboard),
          element: <RegisterTrialClassPage />
        },
        {
          path: getRouteName(ROUTES.BulkInvoices, ROUTES.Dashboard),
          element: <BulkInvoicesPage />
        },
        {
          path: getRouteName(ROUTES.ManageExam, ROUTES.Dashboard),
          element: <ManageExamPage />
        },
        {
          path: getRouteName(ROUTES.ManageExamClass, ROUTES.Dashboard),
          element: <ManageExamClassPage />
        }
      ],
    },
    {
      // Public invoice view — no AuthContextProvider/AuthGuard so it renders with no login.
      path: getRouteName(ROUTES.PublicInvoiceView),
      element: (
        <ErrorBoundary>
          <PublicInvoiceViewPage />
        </ErrorBoundary>
      ),
    },
    {
      // Public examiner scoring — token-authed via X-Examiner-Token, no AuthContextProvider/AuthGuard.
      path: getRouteName(ROUTES.ExaminerHome),
      element: (
        <ErrorBoundary>
          <ExaminerHomePage />
        </ErrorBoundary>
      ),
    },
    {
      path: getRouteName(ROUTES.ExaminerClass),
      element: (
        <ErrorBoundary>
          <ExaminerClassPage />
        </ErrorBoundary>
      ),
    },
    {
      path: "*",
      element: <Navigate to={ROUTES.Root} replace />,
    },
  ]);

  return <RouterProvider router={routes} />;
};
